import React, {ReactNode} from 'react';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ClientId} from 'notebook/ELNConstants';
import {NoteContext} from 'notebook/notes/core-ui/NoteContext';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import addSamplesToTable, {AddToTableOptions} from 'notebook/sample-propagation/util/addSamplesToTable';
import {
  BaseStructuredTable,
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableAddMenuItems,
  StructuredTableAddOperation,
  StructuredTableAddOperationParameters,
  StructuredTableColumn,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import addRowsToTable from 'notebook/structured-tables/util/addRowsToTable';
import {StructuredTableContexts} from 'notebook/structured-tables/util/useStructuredTableContexts';
import {Cell, Row, Table} from 'notebook/tables/TableConstants';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {AddButton} from 'ui-library/RichObjectTable';

interface AddFromSourceButtonProps<TTableMetadata, TRowMetadata> {
  disabled?: boolean;
  tooltip: string | null;
  addOperations: Array<StructuredTableAddOperation<TTableMetadata, TRowMetadata>>;
  onSelectParams: Omit<StructuredTableAddOperationParameters<TTableMetadata, TRowMetadata>, 'note'>;
}

function MouseEventBlocker({children}: {children: ReactNode}): JSX.Element {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onMouseUp={(e) => {
        e.stopPropagation();
      }}
    >
      {children}
    </div>
  );
}

export function AddFromSourceButton<TTableMetadata, TRowMetadata>(
  props: AddFromSourceButtonProps<TTableMetadata, TRowMetadata>
): JSX.Element {
  const {note} = NoteContext.useContext();
  return (
    <TooltipWrapper tooltipTitle={props.tooltip}>
      {/* When the button is clicked, we shouldn't select the whole table (BNCH-16188) */}
      <MouseEventBlocker>
        <Dropdown
          className="workflowTable-addDropdown"
          menu={
            <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
              {props.addOperations.map(({type, onSelect, dataTestAttrs, options}, index) => (
                <Option
                  icon={StructuredTableAddMenuItems[type].icon}
                  onSelect={() => {
                    onSelect({note: note ?? null, ...props.onSelectParams, options});
                  }}
                  key={index}
                  dataTestAttrs={dataTestAttrs}
                >
                  {options?.menuItemNameOverride ?? StructuredTableAddMenuItems[type].name}
                </Option>
              ))}
            </Menu>
          }
        >
          <AddButton disabled={props.disabled} altText="Add samples" />
        </Dropdown>
      </MouseEventBlocker>
    </TooltipWrapper>
  );
}

export default function getAddFromSourceButton<TTableMetadata, TRowMetadata>({
  addOperations,
  adapter,
  structuredTable,
  table,
  tableIsLocked,
  updateHandler,
  contexts,
  insertAfterTable,
}: {
  addOperations: Array<StructuredTableAddOperation<TTableMetadata, TRowMetadata>>;
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
  structuredTable: BaseStructuredTable<TTableMetadata, TRowMetadata, ClientId>;
  table: Table;
  tableIsLocked: boolean;
  updateHandler: (table: StructuredTable<TTableMetadata, TRowMetadata>) => void;
  contexts: StructuredTableContexts;
  insertAfterTable: (noteParts: Array<NotePart>, cidIdMap?: CidIdMap) => void;
}): JSX.Element | null {
  const onSelectParams: Omit<StructuredTableAddOperationParameters<TTableMetadata, TRowMetadata>, 'note'> = {
    structuredTable,
    addSamples: (
      samplesByColumn: Array<{samples: Array<Cell>; columnIndex: number}>,
      options?: AddToTableOptions
    ) => {
      const updatedTable = addSamplesToTable(structuredTable, adapter, table, samplesByColumn, options);
      if (updatedTable != null) {
        updateHandler(updatedTable);
        analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.ADDED_SAMPLES_TO_STRUCTURED_TABLE);
      }
    },
    addFullRows: (
      rows: Array<Row>,
      rowMetadatas?: Array<TRowMetadata>,
      delegatedColumns?: Array<StructuredTableColumn>,
      structuredTableUpdates?: Partial<
        Pick<StructuredTable<TTableMetadata, TRowMetadata>, 'metadata' | 'schemas'>
      >
    ) => {
      const newStructuredTable = addRowsToTable(rows, structuredTable, adapter, rowMetadatas ?? null);
      if (newStructuredTable != null) {
        updateHandler({
          ...newStructuredTable,
          // if additional table data is provided, use it
          ...structuredTableUpdates,
          delegatedColumns: [...newStructuredTable.delegatedColumns, ...(delegatedColumns ?? [])],
        });
      }
    },
    contexts,
    insertAfterTable,
  };

  const showAddButton =
    addOperations.length > 0 &&
    // Parameter Tables always show an add button in config
    (!structuredTable.isConfig || structuredTable.kind === StructuredTableKind.PARAMETER);

  return showAddButton ? (
    <AddFromSourceButton
      addOperations={addOperations}
      onSelectParams={onSelectParams}
      disabled={tableIsLocked}
      tooltip={null}
    />
  ) : null;
}
